/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { ChallengeResponse, TranslatedString } from "@gnu-taler/taler-util";
import { AsyncAction } from "@gnu-taler/web-util/browser";
import { ComponentChildren, createContext, h, VNode } from "preact";
import { useContext, useRef, useState } from "preact/hooks";

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

export type ContextType = {
  pending?: MfaState;
  cancel(): void;
  regenerate(manual?: boolean): Promise<boolean>;
  onNewChallenge(
    operation: TranslatedString,
    username: string,
    challenge: ChallengeResponse,
    handler: AsyncAction<[string[]]>,
  ): void;
};

const initial: ContextType = {
  onNewChallenge: () => {
    throw Error("BankChallengeHandlerProvider not initialized");
  },
  cancel: () => {
    throw Error("BankChallengeHandlerProvider not initialized");
  },
  regenerate: async () => false,
};
const Context = createContext<ContextType>(initial);

export const useBankChallengeHandlerContext = (): ContextType =>
  useContext(Context);

type MfaState = {
  requirement: ChallengeResponse;
  username: string;
  title: TranslatedString;
  retry: AsyncAction<[string[]]>;
  regenerationCount: number;
};

export const BankChallengeHandlerProvider = ({
  children,
}: {
  children: ComponentChildren;
}): VNode<any> => {
  const [state, setState] = useState<MfaState>();
  const regenerationInFlight = useRef<number>();
  const challengeGeneration = useRef(0);

  /**
   * Check the response of the handler and
   * expect a MFA response and verify:
   *  - if there is only one challenge
   *  - or multiple challenge and all needs to be completed
   * then start for the first challenge right away.
   *
   * The first challenge should always be the cheaper based on server
   * selection.
   *
   */
  async function onNewChallenge(
    operation: TranslatedString,
    username: string,
    requirement: ChallengeResponse,
    handler: AsyncAction<[string[]]>,
  ) {
    challengeGeneration.current++;
    setState({
      username,
      title: operation,
      requirement,
      retry: handler,
      regenerationCount: regenerationInFlight.current ?? 0,
    });
  }

  function cancel() {
    state?.retry.cancel();
    setState(undefined);
  }

  async function regenerate(manual = false): Promise<boolean> {
    if (!state || (!manual && state.regenerationCount >= 1)) return false;
    const previous = state;
    const previousGeneration = challengeGeneration.current;
    const regenerationCount = state.regenerationCount + 1;
    regenerationInFlight.current = regenerationCount;
    setState(undefined);
    try {
      await previous.retry.run([]);
      const restarted = challengeGeneration.current > previousGeneration;
      if (!restarted) {
        setState({ ...previous, regenerationCount });
      }
      return restarted;
    } finally {
      regenerationInFlight.current = undefined;
    }
  }

  return h(Context.Provider, {
    value: {
      pending: state,
      cancel,
      regenerate,
      onNewChallenge,
    },
    children,
  });
};
